import { useState, useEffect } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Users } from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import Seo from '../components/Seo';
import { pickMeta } from '../seoMeta';

function Login({ setUser }) {
  const { t, i18n } = useTranslation();
  const { lang } = useParams();
  const currentLang = lang || i18n.language || 'ru';
  
  const [loginMode, setLoginMode] = useState('standard'); // 'standard' | 'manager'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [loading, setLoading] = useState(false);
  // 2FA step-up state
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const navigate = useNavigate();

  // Show a toast if the user was hard-redirected here by the App-level 401 interceptor.
  // App.js sets '__session_expired_toast' in sessionStorage synchronously before location.replace,
  // so the message survives the full page reload.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('__session_expired_toast') === '1') {
        toast.error('Сессия истекла. Войдите заново, чтобы продолжить.', { duration: 5000 });
        sessionStorage.removeItem('__session_expired_toast');
      }
    } catch { /* storage disabled */ }
  }, []);

  const completeLogin = (data) => {
    localStorage.setItem('token', data.token);
    localStorage.setItem('user', JSON.stringify(data.user));
    setUser(data.user);
    toast.success(t('auth.welcomeBack'));
    const userRole = data.user.role;
    setTimeout(() => {
      const adminRoles = ['Admin', 'Manager', 'Support', 'Courier'];
      if (userRole === 'ContentCreator') {
        navigate(`/${currentLang}/media-studio`);
      } else if (adminRoles.includes(userRole)) {
        navigate(`/${currentLang}/admin`);
      } else {
        navigate(`/${currentLang}`);
      }
    }, 100);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = loginMode === 'manager'
        ? { email: username, password, company_code: companyCode }
        : { email, password };
      
      const response = await axios.post(`${API}/auth/login`, payload);
      if (response.data.mfa_required) {
        setMfaToken(response.data.mfa_token);
        setLoading(false);
        return;
      }
      // Backend used to return user directly; now /me is needed for that.
      // Existing behaviour: response.data.user was included on non-2FA login.
      const meRes = response.data.user
        ? response.data
        : { token: response.data.token,
            user: (await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${response.data.token}` } })).data };
      completeLogin(meRes);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('auth.loginError'));
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    if (!mfaCode.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/2fa/verify-login`, {
        mfa_token: mfaToken,
        code: mfaCode.trim(),
        use_recovery_code: useRecoveryCode,
      });
      const meRes = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${res.data.token}` },
      });
      completeLogin({ token: res.data.token, user: meRes.data });
      if (res.data.recovery_codes_remaining !== undefined && res.data.recovery_codes_remaining <= 2 && useRecoveryCode) {
        toast.warning(`Осталось ${res.data.recovery_codes_remaining} кодов восстановления — перегенерируйте их в настройках`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ошибка проверки кода');
      if (error.response?.status === 401) {
        // MFA token expired — reset to password step
        setMfaToken(null);
        setMfaCode('');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {(() => { const m = pickMeta('login', currentLang); return <Seo lang={currentLang} path="login" title={m.title} description={m.description} />; })()}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to={`/${currentLang}`} className="flex items-center">
            <img 
              src="/logo.jpg" 
              alt="Ainanan" 
              className="h-10 object-contain"
            />
          </Link>
          <LanguageSwitcher />
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Link to={`/${currentLang}`} className="inline-flex items-center text-stone-600 hover:text-stone-800 mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('auth.backToCatalog')}
          </Link>

          <div className="bg-white rounded-xl shadow-sm border p-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('auth.loginTitle')}</h1>
            <p className="text-gray-500 mb-4">{t('auth.loginSubtitle')}</p>

            {mfaToken ? (
              <form onSubmit={handleMfaSubmit} className="space-y-4" data-testid="mfa-form">
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-900">
                  🔐 Двухфакторная защита включена. Введите {useRecoveryCode ? 'код восстановления' : '6-значный код из Authenticator'}.
                </div>
                <div>
                  <Label htmlFor="mfa-code" className="text-gray-700">
                    {useRecoveryCode ? 'Код восстановления' : 'Код из приложения'}
                  </Label>
                  <Input
                    id="mfa-code"
                    type="text"
                    inputMode={useRecoveryCode ? 'text' : 'numeric'}
                    autoComplete="one-time-code"
                    autoFocus
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value)}
                    placeholder={useRecoveryCode ? 'abc123XYZ-' : '000000'}
                    maxLength={useRecoveryCode ? 20 : 6}
                    className="mt-1 tracking-widest text-center text-lg"
                    data-testid="mfa-code-input"
                    required
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    className="text-emerald-700 hover:text-emerald-800 underline"
                    onClick={() => { setUseRecoveryCode(!useRecoveryCode); setMfaCode(''); }}
                    data-testid="mfa-toggle-recovery"
                  >
                    {useRecoveryCode ? 'Ввести код из приложения' : 'Использовать код восстановления'}
                  </button>
                  <button
                    type="button"
                    className="text-gray-500 hover:text-gray-700"
                    onClick={() => { setMfaToken(null); setMfaCode(''); setUseRecoveryCode(false); }}
                    data-testid="mfa-cancel"
                  >
                    Отмена
                  </button>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !mfaCode.trim()}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5"
                  data-testid="mfa-submit-button"
                >
                  {loading ? 'Проверка…' : 'Подтвердить'}
                </Button>
              </form>
            ) : (
            <>
            {/* Mode toggle */}
            <div className="flex gap-2 mb-6 p-1 bg-gray-100 rounded-lg" data-testid="login-mode-toggle">
              <button
                type="button"
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${loginMode === 'standard' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setLoginMode('standard')}
                data-testid="login-mode-standard"
              >
                Email
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-1 ${loginMode === 'manager' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}
                onClick={() => setLoginMode('manager')}
                data-testid="login-mode-manager"
              >
                <Users className="w-3.5 h-3.5" />
                Менеджер сети
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {loginMode === 'standard' ? (
                /* Standard login: email or phone + password */
                <div>
                  <Label htmlFor="email" className="text-gray-700">{t('auth.emailOrPhone') || 'Email или телефон'}</Label>
                  <Input
                    id="email"
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="your@email.com или +77011234567"
                    className="mt-1"
                    data-testid="login-email-input"
                  />
                </div>
              ) : (
                /* Manager login: username + company_code */
                <>
                  <div>
                    <Label htmlFor="username" className="text-gray-700">Логин</Label>
                    <Input
                      id="username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      placeholder="aidos"
                      className="mt-1"
                      data-testid="login-username-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company_code" className="text-gray-700">Код компании</Label>
                    <Input
                      id="company_code"
                      type="text"
                      value={companyCode}
                      onChange={(e) => setCompanyCode(e.target.value.toLowerCase())}
                      required
                      placeholder="alma"
                      className="mt-1"
                      data-testid="login-company-code-input"
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="password" className="text-gray-700">{t('auth.password')}</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="mt-1"
                  data-testid="login-password-input"
                />
                {loginMode === 'standard' && (
                  <div className="mt-1 text-right">
                    <Link 
                      to={`/${currentLang}/forgot-password`} 
                      className="text-sm text-emerald-700 hover:text-emerald-800"
                      data-testid="forgot-password-link"
                    >
                      {t('auth.forgotPassword') || 'Забыли пароль?'}
                    </Link>
                  </div>
                )}
                {loginMode === 'manager' && (
                  <p className="text-xs text-gray-400 mt-1">Забыли пароль? Обратитесь к владельцу компании</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-medium py-2.5"
                data-testid="login-submit-button"
              >
                {loading ? t('auth.loggingIn') : t('auth.loginButton')}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                {t('auth.noAccount')}{' '}
                <Link to={`/${currentLang}/register`} className="font-medium text-emerald-700 hover:text-emerald-800" data-testid="register-link">
                  {t('auth.registerLink')}
                </Link>
              </p>
            </div>
            </>
            )}
          </div>
        </div>
      </div>

      <footer className="bg-white border-t py-4">
        <div className="max-w-7xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} Ainanan. {t('common.allRights')}.
        </div>
      </footer>
    </div>
  );
}

export default Login;
